import { TransactionCategory } from '@shipstation/financial-services-ui-components';
import axios from 'axios';

export const postUpdatePayment = (data, walletId) => {
  return axios.put(`/wallet/${walletId}/payment`, data);
};

export const postPromoCodeValidation = async (promoCode) => {
  const response = await axios.post('/validateUnifiedPromoCode', { promoCode });
  return response?.data ?? {};
};

export const postRegistration = async (registrationForm) => {
  const response = await axios.post('/wallet/registration', registrationForm);
  return response?.data ?? {};
};

/**
 * @typedef {object} WalletRegistrationResult
 * @property {object} insuranceProviderRegistrationResponses
 * @property {object} labelProviderRegistrationResponses
 * @property {object} resourceUpdateProviderRegistrationResponses
 * @property {string} walletId
 */

/**
 * @typedef {object} PaymentsRegistrationResponse
 * @property {boolean} success - Indicates if the update was successful
 * @property {string|null} [errorMessage] - Error message if the update failed
 * @property {WalletRegistrationResult} [walletResult] - The resulting wallet object if registration was successful
 */

/**
 * Registers wallet using an Auctane Pay payment method
 * @param {unknown} registrationForm
 * @param {boolean} isUpdate - Indicates if this is an update to an existing wallet, say for Terms and Conditions acceptance
 * @returns {Promise<PaymentsRegistrationResponse>}
 */
export const registerWalletPaymentMethod = async (registrationForm, isUpdate) => {
  const additionalMetadata = registrationForm?.MetaData?.additionalMetadata;
  const sessionId = additionalMetadata?.sessionId;
  const paymentMethodId = additionalMetadata?.paymentMethodId;

  const request = {
    walletRegisterRequest: { ...registrationForm, isUpdate },
    paymentPreferenceTypes: [TransactionCategory.AddFunds],
    paymentMethodId,
    sessionId,
    currencyCode: registrationForm.CurrencyCode,
    nickname: registrationForm.Nickname,
    billingAddress: registrationForm.BillingAddress,
  };
  const response = await axios.post('/v2/payments/registration', request);
  return response?.data ?? {};
};

export const postAddressValidation = async (address) => {
  let arrayAddress = address;
  if (!Array.isArray(address)) {
    arrayAddress = [address];
  }
  const response = await axios.post('/address/validate', arrayAddress);
  return response?.data[0] ?? {};
};
export const getWalletEligibility = async (currencyCode) => {
  const response = await axios.get(`/wallet/eligibility/v2?CurrencyCode=${currencyCode}`);
  return response?.data ?? {};
};

export const getWalletRequirements = async (walletId, currencyCode, labelProviderIds, resourceUpdateIds, insuranceProviderIds) => {
  const response = await axios.get('/wallet/registration/requirements/', {
    params: {
      walletId,
      currencyCode,
      labelProviderIds,
      resourceUpdateIds,
      insuranceProviderIds,
    },
  });

  return response?.data ?? {};
};

export const getAllWalletSettings = async () => {
  const response = await axios.get('/wallet/settings/available');
  return response?.data ?? {};
};

export const getAllWalletBalances = async () => {
  const response = await axios.get('/wallet/balances');
  return response?.data ?? [];
};

export const getWalletReportLink = async (walletId) => {
  const response = await axios.get(`/wallet/${walletId}/reports/url`);
  return response?.data ?? {};
};

export const refreshSellerProviderServices = async (sellerProviderId) => {
  const response = await axios.put(`/sellerprovider/${sellerProviderId}/refresh/shippingServices`);
  return response?.data ?? {};
};

/**
 * Get seller provider account link with optional URL parameters
 * @param {string} sellerProviderId - The seller provider ID
 * @param {object} [options] - Optional parameters
 * @param {string} [options.urlType] - The URL type (e.g., 'WebClientPreferences')
 * @param {string} [options.applicationContext] - The application context (e.g., 'carrier/noheader')
 * @returns {Promise<object>} The account link response
 */
export const getSellerProviderAccountLink = async (sellerProviderId, options = {}) => {
  const params = new URLSearchParams();

  if (options.urlType) {
    params.append('urlType', options.urlType);
  }

  if (options.applicationContext) {
    params.append('applicationContext', options.applicationContext);
  }

  const queryString = params.toString();
  const url = `/sellerprovider/${sellerProviderId}/account/url${queryString ? `?${queryString}` : ''}`;

  const response = await axios.get(url);
  return response?.data ?? {};
};

export const updateSellerProviderPassword = async (sellerProviderId, newPassword) => {
  const response = await axios.put(`/sellerprovider/${sellerProviderId}/password`, {
    updatedPassword: newPassword,
  });
  return response?.data ?? {};
};

export const getWallets = async () => {
  const response = await axios.get('/wallet');
  return response?.data ?? [];
};

export const manageAutoFunding = async (walletId, config) => {
  const response = await axios.put(`/wallet/${walletId}/autofunding`, {
    autoFundConfig: config,
  });
  return response?.data ?? {};
};

export const fundAccount = async (walletId, amount) => {
  const response = await axios.post(`/wallet/${walletId}/fund`, { amount });
  return response?.data ?? {};
};

export const saveDefaultFundingAmount = async (walletId, amount) => {
  const response = await axios.put(`/wallet/${walletId}/defaultPurchase`, {
    amount,
  });
  return response?.data ?? {};
};

export const normalizeWalletData = async () => {
  const response = await axios.post('/wallet/normalize');
  return response?.data ?? {};
};

export const disableCarrier = async (body) => {
  const response = await axios.post('/wallet/features/deactivate', body);
  return response?.data ?? [];
};

export const enableCarrier = async (body) => {
  const response = await axios.post('/wallet/features/activate', body);
  return response?.data ?? [];
};

export const getCarrierStatus = async () => {
  const response = await axios.get('/wallet/features');
  return response?.data ?? [];
};

/**
 * @param {string} sellerProviderId - The seller provider ID
 * @returns {Promise<{IsOnboarded: boolean, ErrorMessage: string}>} The onboard status response
 */
export const getUspsOnboardStatus = async (sellerProviderId) => {
  const response = await axios.get(`/provider/uspsOnboardStatus?sellerProviderId=${sellerProviderId}`);
  return response?.data ?? { IsOnboarded: false, ErrorMessage: 'Unknown error' };
};

/**
 * @param {string} walletId - The wallet ID
 * @param {string} nickname - The new nickname
 * @returns {Promise<object>} The updated wallet data
 */
export const updateWalletNickname = async (walletId, nickname) => {
  const response = await axios.put(`/wallet/${walletId}/nickname`, { nickname });
  return response?.data ?? {};
};
